import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CategoryChip } from "@/components/CommentCategories";
import { Printer, ArrowLeft } from "lucide-react";

const STATUS_LABEL = { delivered: "Dorëzuar", late: "Me Vonesë", not_good: "Jo mirë", copy: "Kopje", missed: "Mungon", undelivered: "—", upcoming: "—" };
const HW_DOT_COLOR = { delivered: "#2A9D8F", late: "#E9C46A", not_good: "#FB923C", copy: "#C084FC", missed: "#E76F51", undelivered: "#E5E7EB", upcoming: "#E5E7EB" };

const monthLabel = (ym) => {
    if (!ym || ym === "all") return "Raport i përgjithshëm";
    const [y, m] = ym.split("-");
    const months = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
    return `${months[parseInt(m) - 1]} ${y}`;
};

export default function ReportPrint() {
    const { id } = useParams();
    const [search, setSearch] = useSearchParams();
    const [data, setData] = useState(null);
    const [absences, setAbsences] = useState({ absences: [], total_hours: 0, total_count: 0 });
    const [comments, setComments] = useState({ comments: [], total: 0 });
    const [loading, setLoading] = useState(true);

    const monthParam = search.get("month") || "all";
    const [month, setMonth] = useState(monthParam);

    useEffect(() => {
        Promise.all([
            api.get(`/students/${id}/records`),
            api.get(`/students/${id}/absences`),
            api.get(`/students/${id}/comments`),
        ]).then(([r1, r2, r3]) => {
            setData(r1.data); setAbsences(r2.data); setComments(r3.data);
        }).finally(() => setLoading(false));
    }, [id]);

    const { displayComments, displayAbsences } = useMemo(() => {
        if (month === "all") return { displayComments: comments.comments, displayAbsences: absences.absences };
        return {
            displayComments: comments.comments.filter((c) => c.created_at?.startsWith(month)),
            displayAbsences: absences.absences.filter((a) => a.date?.startsWith(month)),
        };
    }, [month, comments, absences]);

    const availableMonths = useMemo(() => {
        const set = new Set();
        comments.comments.forEach((c) => c.created_at && set.add(c.created_at.slice(0, 7)));
        absences.absences.forEach((a) => a.date && set.add(a.date.slice(0, 7)));
        return Array.from(set).sort().reverse();
    }, [comments, absences]);

    const onChangeMonth = (m) => {
        setMonth(m);
        if (m === "all") setSearch({});
        else setSearch({ month: m });
    };

    if (loading) return <div className="p-8">Duke ngarkuar…</div>;
    if (!data) return <div className="p-8">Nuk u gjet.</div>;

    const today = new Date().toISOString().slice(0, 10);
    const hasMonthData = month === "all" || displayComments.length > 0 || displayAbsences.length > 0;

    return (
        <div className="min-h-screen bg-white">
            <style>{`@media print { .no-print { display: none !important; } .page-break { page-break-after: always; } body { background: white !important; } } @page { size: A4; margin: 14mm; }`}</style>

            <div className="no-print sticky top-0 bg-bone/90 backdrop-blur border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3" style={{ zIndex: 50 }}>
                <button onClick={() => window.history.back()} className="text-sm text-slate-700 hover:text-oxford inline-flex items-center gap-1.5" data-testid="report-back">
                    <ArrowLeft className="w-4 h-4" /> Mbrapa
                </button>
                <div className="flex items-center gap-2">
                    <label className="text-xs uppercase tracking-widest text-slate-600 font-bold">Periudha</label>
                    <select value={month} onChange={(e) => onChangeMonth(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold" data-testid="report-month-select">
                        <option value="all">Raport i përgjithshëm (gjithçka)</option>
                        {availableMonths.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                    </select>
                    <button onClick={() => window.print()} className="bg-oxford text-white text-sm font-semibold rounded-md px-3 sm:px-4 py-2 inline-flex items-center gap-2 hover:bg-oxfordhover" data-testid="report-print-btn">
                        <Printer className="w-4 h-4" /> Printo / Ruaj PDF
                    </button>
                </div>
            </div>

            <div className="max-w-[210mm] mx-auto px-6 sm:px-8 py-8 text-slate-900" style={{ fontFamily: "Figtree, sans-serif" }}>
                <div className="flex items-center justify-between border-b-2 border-oxford pb-4 mb-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{monthLabel(month)}</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black text-oxford">German Gymnasium</h1>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                        <div>Data: <span className="font-mono">{today}</span></div>
                        <div className="mt-1">Nxënës: <span className="font-bold text-slate-800">{data.student.name}</span></div>
                        <div>Viti: {data.student.year}</div>
                    </div>
                </div>

                <section className="mb-6">
                    <h2 className="font-heading text-xl font-bold text-oxford mb-3">Përmbledhje</h2>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="border border-slate-300 rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Lëndë</div>
                            <div className="font-bold text-2xl">{data.records.length}</div>
                        </div>
                        <div className="border border-slate-300 rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Orë mungesë</div>
                            <div className="font-bold text-2xl text-terracotta">{displayAbsences.reduce((s, a) => s + (a.hours || 1), 0)}</div>
                        </div>
                        <div className="border border-slate-300 rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Komente</div>
                            <div className="font-bold text-2xl text-seablue">{displayComments.length}</div>
                        </div>
                    </div>
                </section>

                {!hasMonthData && month !== "all" && (
                    <div className="bg-bone/40 border border-slate-300 rounded-lg p-6 text-center mb-6">
                        <div className="text-sm text-slate-600">
                            Pa të dhëna për {monthLabel(month)}. Provoni një muaj tjetër ose <button onClick={() => onChangeMonth("all")} className="text-oxford font-bold underline">raport i përgjithshëm</button>.
                        </div>
                    </div>
                )}

                {month === "all" && (
                    <>
                        <section className="mb-6">
                            <h2 className="font-heading text-xl font-bold text-oxford mb-3">Notat sipas lëndës</h2>
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="bg-bone border-b-2 border-slate-400">
                                        <th className="text-left p-2">Lënda</th>
                                        <th className="p-2">Sem 1 N1</th><th className="p-2">Sem 1 N2</th><th className="p-2">Sem 1 Përf.</th>
                                        <th className="p-2">Sem 2 N1</th><th className="p-2">Sem 2 N2</th><th className="p-2">Sem 2 Përf.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.records.map((r) => (
                                        <tr key={r.id} className="border-b border-slate-200">
                                            <td className="p-2 font-semibold">{r.subject_name}</td>
                                            <td className="p-2 text-center">{r.computed["1"].grade1 ?? "—"}</td>
                                            <td className="p-2 text-center">{r.computed["1"].grade2 ?? "—"}</td>
                                            <td className="p-2 text-center font-bold text-oxford">{r.computed["1"].final ?? "—"}</td>
                                            <td className="p-2 text-center">{r.computed["2"].grade1 ?? "—"}</td>
                                            <td className="p-2 text-center">{r.computed["2"].grade2 ?? "—"}</td>
                                            <td className="p-2 text-center font-bold text-oxford">{r.computed["2"].final ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>

                        <section className="mb-6">
                            <h2 className="font-heading text-xl font-bold text-oxford mb-3">Detajet e detyrave (Sem 1)</h2>
                            <div className="space-y-2">
                                {data.records.map((r) => (
                                    <div key={r.id} className="border border-slate-200 rounded-lg p-2 text-xs">
                                        <div className="flex items-center justify-between">
                                            <div className="font-bold">{r.subject_name}</div>
                                            <div className="text-[10px] text-slate-500">Cikli 1: {r.computed["1"].hw1.toFixed(2)}/5 • Cikli 2: {r.computed["1"].hw2.toFixed(2)}/5</div>
                                        </div>
                                        <div className="mt-1 flex gap-3">
                                            {["1", "2"].map((c) => (
                                                <div key={c} className="flex items-center gap-1">
                                                    <span className="text-[9px] uppercase tracking-widest text-slate-500 mr-1">N{c}</span>
                                                    {r.semesters["1"][c].homework.map((s, i) => (
                                                        <span key={i} title={STATUS_LABEL[s]} style={{ width: 10, height: 10, borderRadius: 999, background: HW_DOT_COLOR[s] || "#E5E7EB", display: "inline-block" }} />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}

                <section className="mb-6 page-break">
                    <h2 className="font-heading text-xl font-bold text-oxford mb-3">Mungesat ({displayAbsences.length})</h2>
                    {displayAbsences.length === 0 ? (
                        <div className="text-sm text-slate-500">Pa mungesa në këtë periudhë.</div>
                    ) : (
                        <table className="w-full text-xs border-collapse">
                            <thead>
                                <tr className="bg-bone border-b-2 border-slate-400">
                                    <th className="text-left p-2">Data</th><th className="text-left p-2">Lënda</th>
                                    <th className="p-2">Orë</th><th className="text-left p-2">Komenti</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayAbsences.map((a) => (
                                    <tr key={a.id} className="border-b border-slate-200">
                                        <td className="p-2 font-mono">{a.date}</td>
                                        <td className="p-2">{a.subject_name}</td>
                                        <td className="p-2 text-center">{a.hours}</td>
                                        <td className="p-2">{a.comment || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                <section className="mb-4">
                    <h2 className="font-heading text-xl font-bold text-oxford mb-3">Komentet e mësuesve ({displayComments.length})</h2>
                    {displayComments.length === 0 ? (
                        <div className="text-sm text-slate-500">Pa komente në këtë periudhë.</div>
                    ) : (
                        <div className="space-y-2">
                            {displayComments.map((c) => (
                                <div key={c.id} className="border border-slate-200 rounded-lg p-3 text-xs">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="font-bold">{c.subject_name}</span>
                                        <span className="text-slate-500">• {c.teacher_name} ({c.teacher_initials})</span>
                                        <span className="text-slate-400 font-mono">{c.created_at?.slice(0, 10)}</span>
                                        <CategoryChip category={c.category} />
                                    </div>
                                    {c.text && <p className="text-slate-700">{c.text}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <div className="text-center text-[10px] text-slate-400 mt-8 pt-4 border-t border-slate-200">
                    German Gymnasium • Platformë Notash • Periudha: {monthLabel(month)} • {today}
                </div>
            </div>
        </div>
    );
}
